import React, { useState, useMemo, useEffect, useCallback } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, ScatterChart, Scatter,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  Treemap, Legend
} from "recharts";
import PageLayout from "../components/PageLayout";
import LoadingOverlay from "../components/LoadingOverlay";
import { useSharedDataset } from "../DataContext";
import { theme } from "../theme";

// ── Helpers ──────────────────────────────────────────────────────────────────

const C = theme.colors.chartColors;
const NATURE_BLUE = "#6CA2AF";

const Card = ({ children, style = {} }) => (
  <div style={{
    background: theme.colors.card,
    border: `1px solid ${theme.colors.cardBorder}`,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    ...style
  }}>{children}</div>
);

const ChartTitle = ({ children }) => (
  <div style={{
    fontFamily: theme.fonts.display,
    fontSize: 12,
    fontWeight: 700,
    color: theme.colors.accent,
    letterSpacing: 1,
    marginBottom: 16,
    textTransform: "uppercase"
  }}>{children}</div>
);

const StatBadge = ({ label, value, color = theme.colors.accent }) => (
  <div style={{
    background: `${color}11`,
    border: `1px solid ${color}33`,
    borderRadius: 8,
    padding: "12px 18px",
    textAlign: "center",
    minWidth: 120
  }}>
    <div style={{
      fontFamily: theme.fonts.display,
      fontSize: 22,
      fontWeight: 700,
      color,
    }}>{value}</div>
    <div style={{
      fontFamily: theme.fonts.mono,
      fontSize: 10,
      color: theme.colors.textMuted,
      letterSpacing: 1,
      marginTop: 2
    }}>{label}</div>
  </div>
);

const Select = ({ value, onChange, options, label, style = {} }) => (
  <div style={{ ...style }}>
    {label && <div style={{
      fontFamily: theme.fonts.mono, fontSize: 10,
      color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 4
    }}>{label}</div>}
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: theme.colors.inputBg,
        border: `1px solid ${theme.colors.cardBorder}`,
        borderRadius: 6, color: theme.colors.textPrimary,
        fontFamily: theme.fonts.body, fontSize: 13,
        padding: "8px 12px", outline: "none", cursor: "pointer",
        width: "100%"
      }}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`,
      borderRadius: 8, padding: "10px 14px",
      fontFamily: theme.fonts.mono, fontSize: 12
    }}>
      <div style={{ color: theme.colors.textSecondary, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || theme.colors.accent }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

// ── Column Detector ───────────────────────────────────────────────────────────

function detectColumns(columns) {
  const clean = s => s.toLowerCase().replace(/[\s._-]/g, "");
  const find = (...keywords) =>
    columns.find(c => keywords.some(k => clean(c).includes(clean(k))));
  const findAll = (...keywords) =>
    columns.filter(c => keywords.some(k => clean(c).includes(clean(k))));

  // Smart attack type detection
  const attackType = (
    find("attacktype", "attack_type", "label", "category", "class", "threat", "signature", "traffictype", "traffic_type", "dataset") ||
    // Fallback: find a low-cardinality string column not named after network fields
    null
  );

  // Smart port detection — covers Zeek (id.resp_p), Suricata, standard
  const dstPort = find("dstport", "dst_port", "destport", "id.resp_p", "resp_p", "dport", "port");
  const srcPort = find("srcport", "src_port", "sourceport", "id.orig_p", "orig_p", "sport");

  // Smart IP detection — covers Zeek (id.orig_h, id.resp_h)
  const srcIP = find("srcip", "src_ip", "sourceip", "id.orig_h", "orig_h", "attackerip", "clientip");
  const dstIP = find("dstip", "dst_ip", "destip", "id.resp_h", "resp_h", "victimip", "serverip");

  return {
    attackType,
    timestamp:   find("timestamp", "time", "datetime", "date", "ts"),
    srcIP,
    dstIP,
    srcPort,
    dstPort,
    protocol:    find("protocol", "proto", "id.proto", "transport"),
    severity:    find("severity", "risk", "priority", "level", "score"),
    action:      find("action", "verdict", "disposition", "result", "conn_state"),
    bytes:       find("bytes", "orig_bytes", "resp_bytes", "length", "size", "payload"),
    packets:     find("packets", "pkts", "orig_pkts", "resp_pkts", "count"),
    country:     find("country", "geo", "location", "region"),
    user:        find("user", "username", "account", "uid"),
    service:     find("service", "application", "app"),
    numericCols: findAll("byte", "packet", "length", "size", "count", "duration", "port", "score", "pkts", "orig", "resp")
  };
}

// ── Counting helper ───────────────────────────────────────────────────────────

function countByColumn(data, col, topN = 20) {
  if (!col) return [];
  const counts = {};
  data.forEach(row => {
    let v = row[col];
    const s = (v == null || v === "") ? "Unknown" : String(v).trim();
    const key = s || "Unknown";
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([name, value]) => ({ name, value }));
}

// ── Bucket time series ────────────────────────────────────────────────────────

function buildTimeSeries(data, groupCol, buckets = 50) {
  if (!groupCol) return [];
  const groups = {};
  data.forEach((row, i) => {
    const bucket = Math.floor((i / data.length) * buckets);
    const key = String(row[groupCol] || "Unknown").trim() || "Unknown";
    if (!groups[key]) groups[key] = Array(buckets).fill(0);
    groups[key][bucket] = (groups[key][bucket] || 0) + 1;
  });
  return Object.entries(groups)
    .sort((a, b) => b[1].reduce((s, v) => s + v) - a[1].reduce((s, v) => s + v))
    .slice(0, 8)
    .map(([name, vals]) => ({
      name,
      data: vals.map((v, idx) => ({ t: idx, value: v }))
    }));
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AttackAnalytics() {
  const { dataset, setDataset, hasData } = useSharedDataset();
  const rawData  = dataset.data || [];
  const columns  = dataset.columns || [];
  const fileName = dataset.fileName || "";
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [topN, setTopN]           = useState(15);
  const [filterAttack, setFilterAttack] = useState("ALL");
  const [filterSeverity, setFilterSeverity] = useState("ALL");
  const [activeTab, setActiveTab] = useState("overview");

  // ── File Upload ──────────────────────────────────────────────────────────────

  const handleFile = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);

    const done = (data) => {
      setDataset({ data, columns: Object.keys(data[0] || {}), fileName: file.name, file });
      setFilterAttack("ALL");
      setLoading(false);
    };

    if (file.name.toLowerCase().endsWith(".csv")) {
      Papa.parse(file, {
        header: true, skipEmptyLines: true,
        complete: r => done(r.data)
      });
    } else {
      const reader = new FileReader();
      reader.onload = evt => {
        const wb = XLSX.read(evt.target.result);
        const sheet = wb.Sheets[wb.SheetNames[0]];
        done(XLSX.utils.sheet_to_json(sheet));
      };
      reader.readAsArrayBuffer(file);
    }
  }, []);

  // ── Detected columns ─────────────────────────────────────────────────────────

  const detected = useMemo(() => detectColumns(columns), [columns]);

  // ── Attack type list ─────────────────────────────────────────────────────────

  const attackTypes = useMemo(() => {
    if (!detected.attackType) return [];
    return [...new Set(rawData.map(r => String(r[detected.attackType] || "Unknown").trim()))];
  }, [rawData, detected.attackType]);

  // ── Filtered data ─────────────────────────────────────────────────────────────

  const filteredData = useMemo(() => {
    let d = rawData;
    if (filterAttack !== "ALL" && detected.attackType)
      d = d.filter(r => String(r[detected.attackType] || "").trim() === filterAttack);
    if (filterSeverity !== "ALL" && detected.severity)
      d = d.filter(r => String(r[detected.severity] || "").toLowerCase() === filterSeverity.toLowerCase());
    if (search)
      d = d.filter(r => Object.values(r).join(" ").toLowerCase().includes(search.toLowerCase()));
    return d;
  }, [rawData, filterAttack, filterSeverity, search, detected]);

  // ── Stats ─────────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    if (!filteredData.length) return null;
    const total = filteredData.length;
    const attackCounts = detected.attackType ? countByColumn(filteredData, detected.attackType) : [];
    const topAttack = attackCounts[0]?.name || "N/A";
    const topAttackPct = attackCounts[0] ? ((attackCounts[0].value / total) * 100).toFixed(1) : 0;

    const severityCounts = detected.severity
      ? { High: 0, Medium: 0, Low: 0, Unknown: 0 }
      : null;
    if (severityCounts && detected.severity) {
      filteredData.forEach(r => {
        const s = String(r[detected.severity] || "").toLowerCase();
        if (s.includes("high") || s === "3" || s === "critical") severityCounts.High++;
        else if (s.includes("med") || s === "2") severityCounts.Medium++;
        else if (s.includes("low") || s === "1") severityCounts.Low++;
        else severityCounts.Unknown++;
      });
    }

    return { total, topAttack, topAttackPct, attackCounts, severityCounts };
  }, [filteredData, detected]);

  // ── Chart data ────────────────────────────────────────────────────────────────

  const charts = useMemo(() => {
    const d = filteredData;
    if (!d.length) return {};

    // 1. Attack type distribution
    const attackDist = countByColumn(d, detected.attackType, topN);

    // 2. Severity distribution
    const severityDist = countByColumn(d, detected.severity, 10);

    // 3. Protocol distribution
    const protocolDist = countByColumn(d, detected.protocol, 10);

    // 4. Action/verdict
    const actionDist = countByColumn(d, detected.action, 10);

    // 5. Src port top
    const srcPortDist = countByColumn(d, detected.srcPort, topN);

    // 6. Dst port top
    const dstPortDist = countByColumn(d, detected.dstPort, topN);

    // 7. Source IP top
    const srcIPDist = countByColumn(d, detected.srcIP, topN);

    // 8. Dest IP top
    const dstIPDist = countByColumn(d, detected.dstIP, topN);

    // 9. Country
    const countryDist = countByColumn(d, detected.country, topN);

    // 10. Service/App
    const serviceDist = countByColumn(d, detected.service, topN);

    // 11. User
    const userDist = countByColumn(d, detected.user, topN);

    // 12. Attack x Protocol heatmap data
    const attackProto = [];
    if (detected.attackType && detected.protocol) {
      const map = {};
      d.forEach(r => {
        const a = String(r[detected.attackType] || "Unknown").trim();
        const p = String(r[detected.protocol] || "Unknown").trim();
        if (!map[a]) map[a] = {};
        map[a][p] = (map[a][p] || 0) + 1;
      });
      Object.entries(map).slice(0, 8).forEach(([a, protos]) => {
        Object.entries(protos).forEach(([p, cnt]) => {
          attackProto.push({ attack: a, protocol: p, count: cnt });
        });
      });
    }

    // 13. Time series per attack type (bucket-based)
    const attackTimeline = buildTimeSeries(d, detected.attackType, 40);

    // 14. Severity pie
    const severityPie = severityDist.map((s, i) => ({ ...s, fill: C[i % C.length] }));

    // 15. Dst Port x Attack type (top combinations)
    const portAttackMap = [];
    if (detected.dstPort && detected.attackType) {
      const map = {};
      d.forEach(r => {
        const k = `${r[detected.dstPort] || "?"}_${String(r[detected.attackType] || "?").slice(0, 12)}`;
        map[k] = (map[k] || 0) + 1;
      });
      Object.entries(map)
        .sort((a, b) => b[1] - a[1])
        .slice(0, topN)
        .forEach(([k, v]) => {
          const [port, attack] = k.split("_");
          portAttackMap.push({ name: `${port} / ${attack}`, value: v });
        });
    }

    // 16. Bytes/payload analysis
    const bytesDist = [];
    if (detected.bytes) {
      const ranges = { "0-100": 0, "101-500": 0, "501-1000": 0, "1001-5000": 0, "5000+": 0 };
      d.forEach(r => {
        const v = Number(r[detected.bytes]);
        if (!isNaN(v)) {
          if (v <= 100) ranges["0-100"]++;
          else if (v <= 500) ranges["101-500"]++;
          else if (v <= 1000) ranges["501-1000"]++;
          else if (v <= 5000) ranges["1001-5000"]++;
          else ranges["5000+"]++;
        }
      });
      Object.entries(ranges).forEach(([name, value]) => bytesDist.push({ name, value }));
    }

    // 17. Radar: attack profile per type
    const radarData = [];
    if (detected.attackType && detected.numericCols.length >= 3) {
      const cols = detected.numericCols.slice(0, 5);
      const byAttack = {};
      d.forEach(r => {
        const a = String(r[detected.attackType] || "Unknown").trim();
        if (!byAttack[a]) byAttack[a] = { n: 0, sums: {} };
        byAttack[a].n++;
        cols.forEach(c => { byAttack[a].sums[c] = (byAttack[a].sums[c] || 0) + (Number(r[c]) || 0); });
      });
      cols.forEach(col => {
        const point = { metric: col.slice(0, 12) };
        Object.entries(byAttack).slice(0, 5).forEach(([a, v]) => {
          point[a.slice(0, 10)] = +(v.sums[col] / v.n).toFixed(2) || 0;
        });
        radarData.push(point);
      });
    }

    // 18. Attack frequency treemap
    const treemapData = attackDist.map((d, i) => ({
      name: d.name, size: d.value, fill: C[i % C.length]
    }));

    // 19. Cumulative attack trend (area)
    const cumulArea = [];
    if (detected.attackType) {
      let cum = 0;
      d.forEach((_, i) => {
        cum++;
        if (i % Math.max(1, Math.floor(d.length / 60)) === 0) {
          cumulArea.push({ t: i, count: cum });
        }
      });
    }

    // 20. Column statistics (numeric)
    const numStats = [];
    detected.numericCols.slice(0, 6).forEach(col => {
      const vals = d.map(r => Number(r[col])).filter(v => !isNaN(v) && isFinite(v));
      if (!vals.length) return;
      const sum = vals.reduce((a, b) => a + b, 0);
      const mean = sum / vals.length;
      const sorted = [...vals].sort((a, b) => a - b);
      numStats.push({
        name: col.slice(0, 16),
        min: +sorted[0].toFixed(2),
        max: +sorted[sorted.length - 1].toFixed(2),
        mean: +mean.toFixed(2),
        median: +sorted[Math.floor(sorted.length / 2)].toFixed(2)
      });
    });

    return {
      attackDist, severityDist, protocolDist, actionDist,
      srcPortDist, dstPortDist, srcIPDist, dstIPDist,
      countryDist, serviceDist, userDist, attackProto,
      attackTimeline, severityPie, portAttackMap,
      bytesDist, radarData, treemapData, cumulArea, numStats
    };
  }, [filteredData, detected, topN]);

  // ── Empty state ───────────────────────────────────────────────────────────────

  const tabs = [
    { id: "overview",   label: "Overview" },
    { id: "network",    label: "Network" },
    { id: "timeline",   label: "Timeline" },
    { id: "advanced",   label: "Advanced" },
    { id: "raw",        label: "Raw Data" }
  ];

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <PageLayout title="Attack Analytics" subtitle="CYBER ATTACK INTELLIGENCE · DATASET ANALYSIS">
      <LoadingOverlay visible={loading} fileName={fileName} />

      {/* ── Upload Banner ─────────────────────────────────────────────────────── */}
      <Card style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <label style={{
          padding: "10px 20px",
          background: hasData
            ? "#4F746C"
            : theme.colors.accent,
          borderRadius: 8, cursor: "pointer",
          fontFamily: theme.fonts.display, fontSize: 12, fontWeight: 700,
          color: theme.colors.buttonText, letterSpacing: 1, whiteSpace: "nowrap"
        }}>
          {hasData ? "⬆ REPLACE DATASET" : "⬆ UPLOAD DATASET"}
          <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} />
        </label>

        {fileName && (
          <span style={{ fontFamily: theme.fonts.mono, fontSize: 12, color: hasData ? theme.colors.success : theme.colors.accent }}>
            ▸ {fileName} {hasData && rawData.length > 0 ? `· ${rawData.length.toLocaleString()} rows` : ""}
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* Filters */}
        {rawData.length > 0 && (
          <>
            <input
              placeholder="Search dataset..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`,
                borderRadius: 6, color: theme.colors.textPrimary,
                fontFamily: theme.fonts.body, fontSize: 13,
                padding: "8px 12px", outline: "none", width: 180
              }}
            />
            <Select
              value={filterAttack}
              onChange={setFilterAttack}
              label="ATTACK TYPE"
              style={{ minWidth: 160 }}
              options={[
                { value: "ALL", label: "All Attacks" },
                ...attackTypes.map(a => ({ value: a, label: a }))
              ]}
            />
            <Select
              value={topN}
              onChange={v => setTopN(Number(v))}
              label="TOP N"
              style={{ minWidth: 100 }}
              options={[5, 10, 15, 20].map(n => ({ value: n, label: `Top ${n}` }))}
            />
          </>
        )}
      </Card>

      {loading && (
        <div style={{
          textAlign: "center", padding: 40,
          fontFamily: theme.fonts.mono, color: theme.colors.accent, fontSize: 14
        }}>
          ◌ Parsing dataset...
        </div>
      )}

      {!rawData.length && !loading && (
        <Card style={{ textAlign: "center", padding: "60px 40px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>◈</div>
          <div style={{
            fontFamily: theme.fonts.display, fontSize: 18,
            color: theme.colors.textSecondary, letterSpacing: 2
          }}>UPLOAD A CYBER SECURITY DATASET</div>
          <div style={{
            fontFamily: theme.fonts.mono, fontSize: 12,
            color: theme.colors.textMuted, marginTop: 8
          }}>
            Supports CSV &amp; Excel · Auto-detects IDS, Firewall, SIEM, Splunk exports<br/>
            Columns are auto-detected: attack type, IP, port, protocol, severity &amp; more
          </div>
        </Card>
      )}

      {rawData.length > 0 && stats && (
        <>
          {/* ── Stats Row ──────────────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <StatBadge label="TOTAL RECORDS" value={stats.total.toLocaleString()} />
            <StatBadge label="FILTERED" value={filteredData.length.toLocaleString()} color={theme.colors.warning} />
            <StatBadge label="ATTACK TYPES" value={attackTypes.length} color={theme.colors.danger} />
            <StatBadge label="COLUMNS" value={columns.length} color={NATURE_BLUE} />
            {stats.topAttack !== "N/A" && (
              <StatBadge label="TOP ATTACK" value={stats.topAttack.slice(0, 14)} color={theme.colors.danger} />
            )}
            {stats.topAttackPct > 0 && (
              <StatBadge label="TOP ATTACK %" value={`${stats.topAttackPct}%`} color={theme.colors.warning} />
            )}
          </div>

          {/* ── Detected Columns Info ──────────────────────────────────────────── */}
          <Card style={{ padding: "12px 20px", marginBottom: 20 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
              <span style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1 }}>
                AUTO-DETECTED:
              </span>
              {Object.entries(detected)
                .filter(([k, v]) => v && k !== "numericCols")
                .map(([k, v]) => (
                  <span key={k} style={{
                    padding: "3px 10px",
                    background: "rgba(0,212,255,0.08)",
                    border: "1px solid rgba(0,212,255,0.2)",
                    borderRadius: 4,
                    fontFamily: theme.fonts.mono, fontSize: 10,
                    color: theme.colors.accent
                  }}>
                    {k}: <strong>{Array.isArray(v) ? v.length + " cols" : v}</strong>
                  </span>
                ))}
            </div>
          </Card>

          {/* ── Tabs ──────────────────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${theme.colors.cardBorder}`, paddingBottom: 1 }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "10px 20px",
                  background: activeTab === tab.id ? `rgba(0,212,255,0.1)` : "transparent",
                  border: "none",
                  borderBottom: activeTab === tab.id ? `2px solid ${theme.colors.accent}` : "2px solid transparent",
                  color: activeTab === tab.id ? theme.colors.accent : theme.colors.textMuted,
                  fontFamily: theme.fonts.display,
                  fontSize: 11, fontWeight: 700, letterSpacing: 1,
                  cursor: "pointer", transition: "all 0.2s"
                }}
              >
                {tab.label.toUpperCase()}
              </button>
            ))}
          </div>

          {/* ═══════════ TAB: OVERVIEW ═══════════ */}
          {activeTab === "overview" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

              {/* Attack Distribution Bar */}
              {charts.attackDist?.length > 0 && (
                <Card style={{ gridColumn: "1 / -1" }}>
                  <ChartTitle>Attack Type Distribution</ChartTitle>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={charts.attackDist} margin={{ bottom: 60 }}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11, fontFamily: theme.fonts.mono }}
                        angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.attackDist.map((_, i) => (
                          <Cell key={i} fill={C[i % C.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Severity Pie */}
              {charts.severityPie?.length > 0 && (
                <Card>
                  <ChartTitle>Severity Distribution</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={charts.severityPie}
                        dataKey="value" nameKey="name"
                        cx="50%" cy="50%" outerRadius={100}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: theme.colors.textMuted }}
                      >
                        {charts.severityPie.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Protocol Distribution */}
              {charts.protocolDist?.length > 0 && (
                <Card>
                  <ChartTitle>Protocol Distribution</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={charts.protocolDist} dataKey="value" nameKey="name"
                        cx="50%" cy="50%" outerRadius={100}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {charts.protocolDist.map((_, i) => (
                          <Cell key={i} fill={C[i % C.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Action/Verdict */}
              {charts.actionDist?.length > 0 && (
                <Card>
                  <ChartTitle>Action / Verdict Distribution</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.actionDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={120} tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {charts.actionDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Service */}
              {charts.serviceDist?.length > 0 && (
                <Card>
                  <ChartTitle>Service / Application Distribution</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.serviceDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {charts.serviceDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Bytes distribution */}
              {charts.bytesDist?.some(d => d.value > 0) && (
                <Card>
                  <ChartTitle>Payload Size Distribution (Bytes)</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.bytesDist}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.bytesDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Attack treemap */}
              {charts.treemapData?.length > 0 && (
                <Card style={{ gridColumn: "1 / -1" }}>
                  <ChartTitle>Attack Type Treemap (Frequency)</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <Treemap data={charts.treemapData} dataKey="size" nameKey="name"
                      content={(props) => {
                        try {
                          const { x, y, width, height, name, fill } = props;
                          if (!width || !height || width < 2 || height < 2) return <g />;
                          const label = String(name || "");
                          return width > 30 && height > 20 ? (
                            <g>
                              <rect x={x} y={y} width={width} height={height} fill={fill || C[0]}
                                fillOpacity={0.8} stroke={theme.colors.bg} strokeWidth={2} rx={4} />
                              <text x={x + width / 2} y={y + height / 2} textAnchor="middle"
                                fill="#fff" fontSize={Math.min(12, width / 8)}
                                fontFamily={theme.fonts.mono}>
                                {label.length > 12 ? label.slice(0, 12) + "…" : label}
                              </text>
                            </g>
                          ) : (
                            <g><rect x={x} y={y} width={width} height={height} fill={fill || C[0]} fillOpacity={0.8} rx={2} /></g>
                          );
                        } catch { return <g />; }
                      }}
                    />
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          )}

          {/* ═══════════ TAB: NETWORK ═══════════ */}
          {activeTab === "network" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

              {/* Source IP */}
              {charts.srcIPDist?.length > 0 && (
                <Card>
                  <ChartTitle>Top Source IPs</ChartTitle>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={charts.srcIPDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={130} tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={theme.colors.danger} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Dest IP */}
              {charts.dstIPDist?.length > 0 && (
                <Card>
                  <ChartTitle>Top Destination IPs</ChartTitle>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={charts.dstIPDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={130} tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={theme.colors.warning} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Dst Port */}
              {charts.dstPortDist?.length > 0 && (
                <Card>
                  <ChartTitle>Top Destination Ports</ChartTitle>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={charts.dstPortDist} margin={{ bottom: 40 }}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} angle={-30} textAnchor="end" />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.dstPortDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Src Port */}
              {charts.srcPortDist?.length > 0 && (
                <Card>
                  <ChartTitle>Top Source Ports</ChartTitle>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={charts.srcPortDist} margin={{ bottom: 40 }}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} angle={-30} textAnchor="end" />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.srcPortDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Port x Attack */}
              {charts.portAttackMap?.length > 0 && (
                <Card style={{ gridColumn: "1 / -1" }}>
                  <ChartTitle>Port × Attack Type (Top Combinations)</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.portAttackMap} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={200} tick={{ fill: theme.colors.textSecondary, fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {charts.portAttackMap.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Country */}
              {charts.countryDist?.length > 0 && (
                <Card style={{ gridColumn: "1 / -1" }}>
                  <ChartTitle>Geographic Origin (Top Countries / Regions)</ChartTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={charts.countryDist} margin={{ bottom: 40 }}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={C[6]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          )}

          {/* ═══════════ TAB: TIMELINE ═══════════ */}
          {activeTab === "timeline" && (
            <div>
              {/* Cumulative trend */}
              {charts.cumulArea?.length > 0 && (
                <Card>
                  <ChartTitle>Cumulative Event Volume Over Dataset</ChartTitle>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginBottom: 8 }}>
                    X: Dataset position (row index buckets) — no real timestamps in this dataset · Y: Cumulative total events
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={charts.cumulArea}>
                      <defs>
                        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#9C27B0" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#9C27B0" stopOpacity={0}    />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="t"
                        tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
                        label={{ value: "Row Position (dataset segment)", position: "insideBottom", offset: -4, fill: theme.colors.textMuted, fontSize: 10 }}
                        height={40} />
                      <YAxis
                        tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
                        label={{ value: "Cumulative Events", angle: -90, position: "insideLeft", fill: theme.colors.textMuted, fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area dataKey="count" stroke="#9C27B0" fill="url(#areaGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Per-attack timeline — red/amber for attacks, blue for benign */}
              {charts.attackTimeline?.map((item, i) => {
                const isBenign = ["benign","normal","legitimate"].some(x => item.name.toLowerCase().includes(x));
                const attackColors = ["#D32F2F","#C62828","#E53935","#F57F17","#FF6F00","#BF360C"];
                const benignColors = ["#1565C0","#0D47A1","#1976D2","#0277BD","#01579B","#283593"];
                const color = isBenign ? benignColors[i % benignColors.length] : attackColors[i % attackColors.length];
                return (
                  <Card key={i}>
                    <ChartTitle style={{ color }}>Timeline: {item.name}</ChartTitle>
                    <div style={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textMuted, marginBottom: 6 }}>
                      {isBenign ? "Normal/benign traffic" : "Attack traffic"} · X: 0–39 = 40 equal row segments (each ≈ 2.5% of dataset) · Y: event count per segment
                    </div>
                    <ResponsiveContainer width="100%" height={210}>
                      <AreaChart data={item.data}>
                        <defs>
                          <linearGradient id={`tGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={color} stopOpacity={0.35} />
                            <stop offset="95%" stopColor={color} stopOpacity={0}    />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="t"
                          tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
                          label={{ value: "Dataset Segment", position: "insideBottom", offset: -4, fill: theme.colors.textMuted, fontSize: 10 }}
                          height={40} />
                        <YAxis
                          tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
                          label={{ value: "Events", angle: -90, position: "insideLeft", fill: theme.colors.textMuted, fontSize: 10 }} />
                        <Tooltip
                          contentStyle={{ background: "#0A1628", border: `1px solid ${color}44`, fontFamily: "monospace", fontSize: 11 }}
                          formatter={(v) => [`${v} events`, item.name]}
                          labelFormatter={(l) => `Segment ${l}`}
                        />
                        <Area dataKey="value" stroke={color} fill={`url(#tGrad${i})`} strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Card>
                );
              })}

              {!charts.attackTimeline?.length && (
                <Card style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ color: theme.colors.textMuted, fontFamily: theme.fonts.mono }}>
                    No attack type column detected — upload a dataset with a label/attack_type/class column
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* ═══════════ TAB: ADVANCED ═══════════ */}
          {activeTab === "advanced" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

              {/* Radar */}
              {charts.radarData?.length > 0 && (
                <Card style={{ gridColumn: "1 / -1" }}>
                  <ChartTitle>Attack Behavior Profile (Radar — Avg. Numeric Metrics)</ChartTitle>
                  <ResponsiveContainer width="100%" height={340}>
                    <RadarChart data={charts.radarData}>
                      <PolarGrid stroke={theme.colors.cardBorder} />
                      <PolarAngleAxis dataKey="metric" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      {Object.keys(charts.radarData[0] || {})
                        .filter(k => k !== "metric")
                        .map((k, i) => (
                          <Radar key={k} name={k} dataKey={k}
                            stroke={C[i % C.length]} fill={C[i % C.length]} fillOpacity={0.15} />
                        ))}
                      <Legend wrapperStyle={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary }} />
                      <Tooltip content={<CustomTooltip />} />
                    </RadarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Numeric stats */}
              {charts.numStats?.length > 0 && (
                <Card style={{ gridColumn: "1 / -1" }}>
                  <ChartTitle>Numeric Feature Statistics — Normalized (% of Max per Feature)</ChartTitle>
                  <div style={{ fontFamily: theme.fonts.mono, fontSize: 10, color: theme.colors.textMuted, marginBottom: 8 }}>
                    Values normalized per-feature so columns with vastly different scales (e.g. page faults vs. packet counts) are all visible.
                    Hover for actual values.
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={charts.numStats.map(s => ({
                      ...s,
                      minNorm:    s.max !== 0 ? +((s.min    / s.max) * 100).toFixed(1) : 0,
                      meanNorm:   s.max !== 0 ? +((s.mean   / s.max) * 100).toFixed(1) : 0,
                      medianNorm: s.max !== 0 ? +((s.median / s.max) * 100).toFixed(1) : 0,
                      maxNorm:    100,
                      _min: s.min, _mean: s.mean, _median: s.median, _max: s.max
                    }))}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} unit="%" domain={[0,100]} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload;
                        return (
                          <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8, padding: "10px 14px", fontFamily: theme.fonts.mono, fontSize: 11 }}>
                            <div style={{ color: theme.colors.accent, marginBottom: 6, fontWeight: 700 }}>{label}</div>
                            <div style={{ color: C[0] }}>Min:    <strong>{d?._min?.toLocaleString()}</strong></div>
                            <div style={{ color: C[2] }}>Mean:   <strong>{d?._mean?.toLocaleString()}</strong></div>
                            <div style={{ color: C[3] }}>Median: <strong>{d?._median?.toLocaleString()}</strong></div>
                            <div style={{ color: C[1] }}>Max:    <strong>{d?._max?.toLocaleString()}</strong></div>
                          </div>
                        );
                      }} />
                      <Legend wrapperStyle={{ fontFamily: theme.fonts.mono, fontSize: 11, color: theme.colors.textSecondary }} />
                      <Bar dataKey="minNorm"    name="Min"    fill={C[0]} radius={[4,4,0,0]} />
                      <Bar dataKey="meanNorm"   name="Mean"   fill={C[2]} radius={[4,4,0,0]} />
                      <Bar dataKey="medianNorm" name="Median" fill={C[3]} radius={[4,4,0,0]} />
                      <Bar dataKey="maxNorm"    name="Max"    fill={C[1]} radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* User distribution */}
              {charts.userDist?.length > 0 && (
                <Card>
                  <ChartTitle>Top Users / Accounts</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.userDist} layout="vertical">
                      <XAxis type="number" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" fill={C[7]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Protocol x Attack */}
              {charts.protocolDist?.length > 0 && (
                <Card>
                  <ChartTitle>Protocol Breakdown</ChartTitle>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={charts.protocolDist}>
                      <XAxis dataKey="name" tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <YAxis tick={{ fill: theme.colors.textSecondary, fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {charts.protocolDist.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </div>
          )}

          {/* ═══════════ TAB: RAW DATA ═══════════ */}
          {activeTab === "raw" && (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ overflowX: "auto", maxHeight: 600, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: theme.fonts.mono, fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: theme.colors.inputBg, position: "sticky", top: 0, zIndex: 2 }}>
                      {columns.map(col => (
                        <th key={col} style={{
                          padding: "10px 14px", textAlign: "left",
                          color: theme.colors.accent,
                          borderBottom: `1px solid ${theme.colors.cardBorder}`,
                          whiteSpace: "nowrap", letterSpacing: 0.5, fontSize: 12
                        }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.slice(0, 200).map((row, i) => {
                      const isAttack = detected.attackType && row[detected.attackType] &&
                        !String(row[detected.attackType]).toLowerCase().includes("normal") &&
                        !String(row[detected.attackType]).toLowerCase().includes("benign");
                      return (
                        <tr key={i} style={{
                          background: isAttack
                            ? i % 2 === 0 ? "rgba(255,61,87,0.07)" : "rgba(255,61,87,0.04)"
                            : i % 2 === 0 ? theme.colors.rowAltStrong : "transparent",
                          borderBottom: `1px solid rgba(26,39,68,0.5)`
                        }}>
                          {columns.map(col => (
                            <td key={col} style={{
                              padding: "7px 14px",
                              color: col === detected.attackType
                                ? (isAttack ? theme.colors.danger : theme.colors.success)
                                : theme.colors.textPrimary,
                              whiteSpace: "nowrap", maxWidth: 200,
                              overflow: "hidden", textOverflow: "ellipsis"
                            }}>
                              {String(row[col] ?? "")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{
                padding: "10px 16px",
                borderTop: `1px solid ${theme.colors.cardBorder}`,
                fontFamily: theme.fonts.mono, fontSize: 11,
                color: theme.colors.textMuted
              }}>
                Showing {Math.min(200, filteredData.length)} of {filteredData.length} rows
              </div>
            </Card>
          )}

          {/* Footer */}
          <div style={{
            textAlign: "center", marginTop: 32,
            fontFamily: theme.fonts.mono, fontSize: 10,
            color: theme.colors.textMuted, paddingBottom: 20
          }}>
            NexGen by Hassaan Alrifai · © {new Date().getFullYear()} hassaanalrifai.com
          </div>
        </>
      )}
    </PageLayout>
  );
}
